/**
 * Internal-link map for every cost / pricing article.
 *
 * Each entry lists the most relevant *service* pages a reader should be sent
 * to after finishing the article. The LearnArticleLayout reads this map at
 * render time and auto-renders a "Keep exploring" section that also links to
 * the shared /learn/cost-guides hub. That way every cost article links out
 * to (a) the cost-guides hub and (b) the actual money/service pages without
 * needing per-article markup edits.
 */

export type CostRelatedLink = {
  label: string;
  to: string;
  description?: string;
};

export const COST_GUIDES_HUB = {
  to: "/learn/cost-guides",
  label: "All Colorado marketing cost guides",
  description:
    "Honest pricing for web design, SEO, Google Maps, Google Ads, AI search, and more across Colorado.",
} as const;

/** Common service-page links reused across multiple cost articles. */
const S = {
  pricing: { label: "See full pricing & packages", to: "/pricing" },
  webDesign: { label: "Colorado web design services", to: "/web-design" },
  webDesignPricing: { label: "Web design pricing tiers", to: "/web-design-pricing" },
  localSeo: { label: "Local SEO services", to: "/local-seo" },
  gbp: { label: "Google Business Profile / Maps services", to: "/google-maps" },
  ppc: { label: "Google Ads / PPC management", to: "/google-ads-ppc" },
  geoAi: { label: "AI search & GEO services", to: "/geo-ai" },
  services: { label: "All digital marketing services", to: "/services" },
  freeAudit: { label: "Get a free Colorado audit", to: "/free-audit" },
  landscaping: { label: "Landscaping marketing services", to: "/landscaping-marketing-colorado" },
  hvac: { label: "HVAC marketing services", to: "/web-design-for-hvac" },
  roofing: { label: "Roofing marketing services", to: "/web-design-for-roofing" },
  plumbing: { label: "Plumbing marketing services", to: "/web-design-for-plumbing" },
  electrical: { label: "Electrical marketing services", to: "/web-design-for-electrical" },
  hvacSeo: { label: "Local SEO for HVAC", to: "/local-seo-for-hvac" },
  roofingSeo: { label: "Local SEO for roofing", to: "/local-seo-for-roofing" },
  plumbingSeo: { label: "Local SEO for plumbing", to: "/local-seo-for-plumbing" },
  electricalSeo: { label: "Local SEO for electrical", to: "/local-seo-for-electrical" },
} satisfies Record<string, CostRelatedLink>;

/**
 * Keyed by canonical article path. Keep keys identical to the article's
 * `path` prop so lookups are exact-match (no slug munging).
 */
export const COST_ARTICLE_LINKS: Record<string, CostRelatedLink[]> = {
  // ----- Service-cost articles -----
  "/learn/web-design-cost-colorado": [
    S.webDesign,
    S.webDesignPricing,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/monthly-lead-generation-cost-colorado": [
    S.localSeo,
    S.ppc,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/google-ads-management-cost-colorado": [
    S.ppc,
    S.localSeo,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/google-maps-optimization-cost-colorado": [
    S.gbp,
    S.localSeo,
    S.pricing,
  ],
  "/learn/ai-geo-search-cost-colorado": [
    S.geoAi,
    S.localSeo,
    S.pricing,
  ],
  "/learn/social-media-management-cost-colorado": [
    S.services,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/cheap-website-true-cost-colorado": [
    S.webDesign,
    S.webDesignPricing,
    S.pricing,
  ],
  "/learn/diy-vs-hire-marketing-cost-colorado": [
    S.services,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/small-business-marketing-budget-colorado": [
    S.services,
    S.pricing,
    S.freeAudit,
  ],

  // ----- Industry-cost articles -----
  "/learn/hvac-marketing-cost-colorado": [
    S.hvac,
    S.hvacSeo,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/roofing-marketing-cost-colorado": [
    S.roofing,
    S.roofingSeo,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/plumber-marketing-cost-colorado": [
    S.plumbing,
    S.plumbingSeo,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/landscaper-marketing-cost-colorado": [
    S.landscaping,
    S.localSeo,
    S.pricing,
  ],
  "/learn/dental-marketing-cost-colorado": [
    S.localSeo,
    S.webDesign,
    S.pricing,
    S.freeAudit,
  ],
  "/learn/law-firm-marketing-cost-colorado": [
    S.localSeo,
    S.webDesign,
    S.ppc,
    S.pricing,
  ],
  "/learn/carpet-cleaning-marketing-cost-colorado": [
    S.localSeo,
    S.gbp,
    S.pricing,
  ],
  "/learn/contractor-marketing-cost-colorado": [
    S.webDesign,
    S.localSeo,
    S.pricing,
    S.freeAudit,
  ],
};

export function getCostArticleLinks(path: string): CostRelatedLink[] | null {
  return COST_ARTICLE_LINKS[path] ?? null;
}
